import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { motion } from 'framer-motion'
import { useChatStore } from '../../stores/chatStore'
import { searchAgents, generateSearchSummary } from '../../lib/api'

interface ChatInputProps {
  compact?: boolean
  autoFocus?: boolean
}

export default function ChatInput({ compact = false, autoFocus = false }: ChatInputProps) {
  const [value, setValue] = useState('')
  const [callableOnly, setCallableOnly] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { addMessage, setSearchResults, setLoading, isLoading } = useChatStore()

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autoFocus])

  const handleSubmit = async () => {
    const query = value.trim()
    if (isLoading) return

    setValue('')
    addMessage({ role: 'user', content: query || (callableOnly ? 'Show callable agents' : 'Show all agents') })
    setLoading(true)

    try {
      const params = parseQuery(query)
      if (callableOnly) params.callable_only = true
      const result = await searchAgents(params)
      const summary = generateSearchSummary(result.agents, query, result.total)

      addMessage({
        role: 'assistant',
        content: summary,
        agents: result.agents,
        total: result.total,
      })
      setSearchResults(result.agents, result.total, query, params)
    } catch (error) {
      console.error('Search error:', error)
      addMessage({
        role: 'assistant',
        content: 'Sorry, there was an error searching for agents. Please try again.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  if (compact) {
    return (
      <div className="flex items-center gap-3 bg-[var(--bg-secondary)] border border-[var(--border-default)] rounded-xl px-4 py-3 focus-within:border-[var(--accent-teal)] transition-colors">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search for more agents..."
          disabled={isLoading}
          className="flex-1 bg-transparent border-none outline-none font-mono text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] disabled:opacity-50"
        />
        <motion.button
          onClick={handleSubmit}
          disabled={!value.trim() || isLoading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="p-2 rounded-lg bg-[var(--accent-teal)] text-[var(--bg-primary)] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <SendIcon className="w-4 h-4" />
        </motion.button>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-3 bg-[var(--bg-secondary)] border border-[var(--border-default)] rounded-2xl px-5 py-4 focus-within:border-[var(--accent-teal)] focus-within:shadow-lg focus-within:shadow-[var(--accent-teal)]/10 transition-all">
        <SearchIcon className="w-5 h-5 text-[var(--text-muted)]" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What task do you want to accomplish?"
          disabled={isLoading}
          className="flex-1 bg-transparent border-none outline-none font-mono text-base text-[var(--text-primary)] placeholder:text-[var(--text-muted)] disabled:opacity-50"
        />
        <button
          onClick={() => setCallableOnly(!callableOnly)}
          className="flex items-center gap-2 px-2 py-1 rounded-lg text-xs font-mono whitespace-nowrap"
          title={callableOnly ? 'Showing callable agents only' : 'Showing all agents'}
        >
          <span className={`text-xs ${callableOnly ? 'text-[var(--status-online)]' : 'text-[var(--text-muted)]'}`}>Callable</span>
          <div className={`relative w-8 h-[18px] rounded-full transition-colors ${callableOnly ? 'bg-[var(--status-online)]' : 'bg-[var(--text-muted)]/30'}`}>
            <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform ${callableOnly ? 'translate-x-[16px]' : 'translate-x-[2px]'}`} />
          </div>
        </button>
        <motion.button
          onClick={handleSubmit}
          disabled={isLoading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-5 py-2 rounded-xl bg-[var(--accent-teal)] text-[var(--bg-primary)] font-mono text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
        >
          {isLoading ? 'Searching...' : 'Search'}
        </motion.button>
      </div>
    </div>
  )
}

function parseQuery(query: string): { q?: string; protocol?: string; online_only?: boolean; callable_only?: boolean; capability?: string; limit: number } {
  const params: { q?: string; protocol?: string; online_only?: boolean; callable_only?: boolean; capability?: string; limit: number } = { limit: 20 }

  const protocolMatch = query.match(/protocol:(\w+)/i)
  if (protocolMatch) {
    params.protocol = protocolMatch[1].toLowerCase()
    query = query.replace(protocolMatch[0], '').trim()
  }

  const onlineMatch = query.match(/online:(true|false)/i)
  if (onlineMatch) {
    params.online_only = onlineMatch[1].toLowerCase() === 'true'
    query = query.replace(onlineMatch[0], '').trim()
  }

  const capabilityMatch = query.match(/capability:(\S+)/i)
  if (capabilityMatch) {
    params.capability = capabilityMatch[1]
    params.limit = 50
    query = query.replace(capabilityMatch[0], '').trim()
  }

  if (query) {
    params.q = query
  }

  return params
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  )
}
